import { useState, useRef, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { connectSocket } from "@via/utils/socket";
import {
  waitForSocketConnection,
  setupResponseListeners,
  initializeFileContext,
} from "@via/utils/cortex-socket";
import {
  SocketEvents,
  type Message,
  type McpToolCallPendingPayload,
  type McpToolCallResultPayload,
} from "common";
import { buildConversationHistoryWithContext } from "@via/utils/cortex-context-builder";
import type { CortexContextHandler } from "@via/context/cortex-context";

interface UseCortexStreamingOptions {
  currentContext: CortexContextHandler | null;
  selectedTokenId: number | null;
  selectedModel?: string | null;
  knowledgeAssistantId?: number; // Knowledge Builder assistant (multi-file RAG)
  conversationId?: string | null; // Conversation ID (temp-* for pending)
  conversationHistory?: Message[]; // History from context (single source of truth)
  onUserMessage?: (content: string) => void; // Append user message in context
  onAssistantMessage?: (content: string) => void; // Append assistant message in context
  onMcpToolCallMessage?: (message: Message) => void; // Append MCP tool call message in context
  onConversationCreated?: (id: string) => void; // Callback when conversation is created
  activeConversation?: { isPending?: boolean } | null; // Active conversation metadata
}

interface UseCortexStreamingReturn {
  isLoading: boolean;
  error: string | null;
  currentResponse: string;
  progressMessages: string[];
  pendingMcpToolCall: McpToolCallPendingPayload | null;
  isRespondingToMcpToolCall: boolean;
  respondToMcpToolCall: (callId: string, approved: boolean) => void;
  sendQuestion: (question: string) => Promise<void>;
  clearError: () => void;
  clearConversation: () => void;
  clearProgress: () => void;
  cleanup: () => void;
}

/**
 * Hook that handles all Cortex streaming logic
 * Encapsulates socket connection, streaming listeners, context building, and file initialization
 */
export function useCortexStreaming({
  currentContext,
  selectedTokenId,
  selectedModel,
  knowledgeAssistantId,
  conversationId = null,
  conversationHistory = [],
  onUserMessage,
  onAssistantMessage,
  onMcpToolCallMessage,
  onConversationCreated,
  activeConversation = null,
}: UseCortexStreamingOptions): UseCortexStreamingReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentResponse, setCurrentResponse] = useState("");
  const [progressMessages, setProgressMessages] = useState<string[]>([]);
  const [pendingMcpToolCall, setPendingMcpToolCall] =
    useState<McpToolCallPendingPayload | null>(null);
  const [isRespondingToMcpToolCall, setIsRespondingToMcpToolCall] =
    useState(false);

  const queryClient = useQueryClient();

  const pendingMcpToolCallRef = useRef(pendingMcpToolCall);
  pendingMcpToolCallRef.current = pendingMcpToolCall;

  const responseRef = useRef<string>("");
  const hasClearedProgressForThisAnswerRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const mcpCleanupRef = useRef<(() => void) | null>(null);
  const initializedFileContextRef = useRef<string>("");
  const isMountedRef = useRef(true);
  const socketRef = useRef<ReturnType<typeof connectSocket> | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearProgress = useCallback(() => {
    setProgressMessages([]);
  }, []);

  const clearConversation = useCallback(() => {
    setCurrentResponse("");
    setError(null);
    setProgressMessages([]);
    setPendingMcpToolCall(null);
    setIsRespondingToMcpToolCall(false);
  }, []);

  const cleanup = useCallback(() => {
    // Clean up listeners
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    // Clean up MCP tool call listeners
    if (mcpCleanupRef.current) {
      mcpCleanupRef.current();
      mcpCleanupRef.current = null;
    }
  }, []);

  const respondToMcpToolCall = useCallback(
    (callId: string, approved: boolean) => {
      const socket = socketRef.current;
      if (!socket || !socket.connected) return;
      socket.emit(SocketEvents.McpToolCallResponse, { callId, approved });

      if (approved) {
        setIsRespondingToMcpToolCall(true);
      } else {
        const pending = pendingMcpToolCallRef.current;
        if (pending && onMcpToolCallMessage) {
          onMcpToolCallMessage({
            role: "assistant",
            content: "",
            mcpToolCall: {
              callId: pending.callId,
              serverName: pending.serverName,
              toolName: pending.toolName,
              toolDescription: pending.toolDescription,
              arguments: pending.arguments,
              status: "rejected",
            },
          });
        }
        setPendingMcpToolCall(null);
      }
    },
    [onMcpToolCallMessage]
  );

  const sendQuestion = useCallback(
    async (question: string) => {
      if (!question.trim() || isLoading || !selectedTokenId) {
        return;
      }

      const questionText = question.trim();
      setIsLoading(true);
      setError(null);
      setCurrentResponse("");
      setProgressMessages([]);
      hasClearedProgressForThisAnswerRef.current = false;

      // Let caller append the user message to global conversation history
      if (onUserMessage) {
        onUserMessage(questionText);
      }

      // Build updated history for this request using the latest history prop
      const baseHistory = conversationHistory || [];
      const userMessage: Message = { role: "user", content: questionText };
      const updatedHistoryForSocket = [...baseHistory, userMessage];

      try {
        // Get socket connection
        const socket = connectSocket("/file-summarize");
        if (!socket) {
          throw new Error("Socket not available");
        }
        socketRef.current = socket;

        await waitForSocketConnection(socket);

        // Set up MCP tool call listeners
        if (mcpCleanupRef.current) {
          mcpCleanupRef.current();
          mcpCleanupRef.current = null;
        }
        const handleToolCallPending = (payload: McpToolCallPendingPayload) => {
          if (!isMountedRef.current) return;
          setPendingMcpToolCall(payload);
        };
        const handleToolCallResult = (payload: McpToolCallResultPayload) => {
          if (!isMountedRef.current) return;

          const pending = pendingMcpToolCallRef.current;
          if (pending?.callId === payload.callId && onMcpToolCallMessage) {
            onMcpToolCallMessage({
              role: "assistant",
              content: "",
              mcpToolCall: {
                callId: payload.callId,
                serverName: payload.serverName,
                toolName: payload.toolName,
                toolDescription: pending.toolDescription,
                arguments: pending.arguments,
                status: "accepted",
                result: {
                  success: payload.success,
                  durationMs: payload.durationMs,
                },
              },
            });
          }

          setPendingMcpToolCall(null);
          setIsRespondingToMcpToolCall(false);
        };
        socket.on(SocketEvents.McpToolCallPending, handleToolCallPending);
        socket.on(SocketEvents.McpToolCallResult, handleToolCallResult);
        mcpCleanupRef.current = () => {
          socket.off(SocketEvents.McpToolCallPending, handleToolCallPending);
          socket.off(SocketEvents.McpToolCallResult, handleToolCallResult);
        };

        // Get context data (always fresh from getContext function)
        const contextData = currentContext?.getContext?.() || {};
        const isFileContext = !!(contextData?.filePath && contextData?.runUUID);

        // File context initialization
        if (isFileContext && contextData.filePath && contextData.runUUID) {
          const fileContextId = `${contextData.filePath}:${contextData.runUUID}`;
          const hasNoAssistantMessages = !baseHistory.some(
            m => m.role === "assistant"
          );
          const needsInitialization =
            hasNoAssistantMessages ||
            initializedFileContextRef.current !== fileContextId;

          if (needsInitialization) {
            // Seed a helpful "working" message immediately so UI isn't blank
            setProgressMessages(["Preparing context…"]);
            await initializeFileContext(socket, {
              filePath: contextData.filePath,
              runUUID: contextData.runUUID,
              tokenId: selectedTokenId,
              fileSource: contextData.fileSource,
              onProgress: message => {
                if (!isMountedRef.current) return;
                if (!message || !message.trim()) return;
                setProgressMessages(prev => {
                  const trimmed = message.trim();
                  const last = prev[prev.length - 1];
                  if (last === trimmed) return prev;
                  const next = [...prev, trimmed];
                  // Keep the UI snappy: retain only the last ~200 lines
                  return next.length > 200
                    ? next.slice(next.length - 200)
                    : next;
                });
              },
            });
            initializedFileContextRef.current = fileContextId;
          }
        } else {
          initializedFileContextRef.current = "";
        }

        const combinedSystemPrompt = currentContext?.systemPrompt?.trim();

        // Build conversation history with context (including the new user message)
        let finalConversationHistory = isFileContext
          ? (updatedHistoryForSocket.filter(
              m => m.role !== "system"
            ) as Message[])
          : buildConversationHistoryWithContext(
              updatedHistoryForSocket,
              contextData,
              combinedSystemPrompt || undefined,
              undefined // Use default buildContextMessage
            );

        if (isFileContext && combinedSystemPrompt) {
          finalConversationHistory = [
            { role: "system", content: combinedSystemPrompt },
            ...finalConversationHistory,
          ];
        }

        // Clean up any existing listeners
        if (cleanupRef.current) {
          cleanupRef.current();
          cleanupRef.current = null;
        }

        // Track response in ref to avoid stale closure
        responseRef.current = "";
        hasClearedProgressForThisAnswerRef.current = false;

        // Determine if conversation is pending (not yet saved to DB)
        const isPendingConversation =
          conversationId?.startsWith("temp-") || activeConversation?.isPending;

        // Set up streaming listeners with mounted check
        cleanupRef.current = setupResponseListeners(socket, {
          onGenerate: (content: string) => {
            if (!isMountedRef.current) return; // Guard against updates after unmount
            // Once the assistant starts replying, hide the report-processing log
            if (!hasClearedProgressForThisAnswerRef.current) {
              hasClearedProgressForThisAnswerRef.current = true;
              setProgressMessages([]);
            }
            responseRef.current += content;
            setCurrentResponse(responseRef.current);
          },
          onComplete: (payload?: { conversationId?: string }) => {
            if (!isMountedRef.current) return; // Guard against updates after unmount

            // Capture the final response value
            const finalResponse = responseRef.current || "";

            // Clear currentResponse immediately to hide streaming bubble
            setCurrentResponse("");

            // Add to conversation history if we have content
            if (finalResponse && finalResponse.trim().length > 0) {
              if (onAssistantMessage) {
                onAssistantMessage(finalResponse);
              }
            }

            // Handle conversation creation
            if (payload?.conversationId && isPendingConversation) {
              // Backend created a new conversation, replace temp ID with real ID
              if (onConversationCreated) {
                onConversationCreated(payload.conversationId);
              }

              // Ensure the new conversation appears in the paginated list by
              // invalidating the conversations query so it refetches from the API.
              queryClient.invalidateQueries({
                queryKey: ["/api/v1/ai-insight/conversations"],
              });
            }

            // Update loading state and cleanup
            setIsLoading(false);
            responseRef.current = "";
            cleanupRef.current = null;
            hasClearedProgressForThisAnswerRef.current = false;
          },
          onError: (error: { message: string; error?: string }) => {
            if (!isMountedRef.current) return; // Guard against updates after unmount
            setError(error.message || error.error || "An error occurred");
            setIsLoading(false);
            setCurrentResponse("");
            setProgressMessages([]);
            cleanupRef.current = null;
            hasClearedProgressForThisAnswerRef.current = false;
          },
        });

        // Filter out any messages with empty content or MCP tool call (safety check)
        const validConversationHistory = finalConversationHistory.filter(
          m =>
            (m.content && m.content.trim().length > 0) ||
            ("mcpToolCall" in m && m.mcpToolCall)
        );

        // Emit question directly
        // Use selectedModel if provided, otherwise fall back to contextData.model
        const modelToUse = selectedModel || contextData.model;

        // Include conversationId only if not pending (undefined for new conversations)
        socket.emit(SocketEvents.ReportChatQuestion, {
          filePath: contextData.filePath || "",
          runUUID: contextData.runUUID || "",
          question: questionText,
          conversationHistory: validConversationHistory,
          tokenId: selectedTokenId,
          model: modelToUse,
          conversationId: isPendingConversation ? undefined : conversationId,
          contextRoutePattern: currentContext?.routePattern,
          contextName: currentContext?.contextName,
          pageUrl: contextData.pageUrl || undefined,
          knowledgeAssistantId,
          fileSource: contextData.fileSource,
        });
      } catch (err) {
        if (!isMountedRef.current) return; // Guard against updates after unmount
        const errorMessage =
          err instanceof Error ? err.message : "Failed to send question";
        setError(errorMessage);
        setIsLoading(false);
        setCurrentResponse("");
        setProgressMessages([]);
      }
    },
    [
      currentContext,
      selectedTokenId,
      isLoading,
      conversationHistory,
      selectedModel,
      knowledgeAssistantId,
      conversationId,
      activeConversation,
      onUserMessage,
      onAssistantMessage,
      onConversationCreated,
    ]
  );

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  return {
    isLoading,
    error,
    currentResponse,
    progressMessages,
    pendingMcpToolCall,
    isRespondingToMcpToolCall,
    respondToMcpToolCall,
    sendQuestion,
    clearError,
    clearConversation,
    clearProgress,
    cleanup,
  };
}
